import { useEffect, useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ChevronDown,
  MoreHorizontal,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { api, errorMessage } from '@/lib/api/client';
import type { VendorDetail } from '@/lib/api/types';
import { ErrorState, Page, PageBody, PageHeader, Panel, Skeleton } from '@/ui/Panel';
import { Button } from '@/ui/Button';
import { Badge } from '@/ui/Badge';
import { Field, Input, Switch, Textarea } from '@/ui/Field';
import { ConfirmDialog } from '@/ui/Dialog';
import { SubNav, SubNavItem } from '@/ui/Filters';
import { Menu, MenuItem } from '@/ui/Menu';
import { useShortcutHint } from '@/app/StatusLine';
import { relativeTime } from '@/lib/format';
import { vendorKeys } from '@/features/buying/api';
import { OffersTab } from '@/features/vendor-catalogs/OffersTab';
import { HowWeOrderTab } from '@/features/buying/VendorProfileForm';
import { profileSummary } from '@/features/buying/vendor-profile';
import { ProductsWeBuyTab } from '@/features/buying/LinkedProductsTable';

type Tab = 'details' | 'ordering' | 'catalog' | 'offers' | 'parsing';

export const Route = createFileRoute('/_app/vendors/$id')({
  staticData: { crumb: 'Vendor' },
  validateSearch: (s: Record<string, unknown>): { tab?: Tab; q?: string } => ({
    tab:
      s.tab === 'catalog' ||
      s.tab === 'parsing' ||
      s.tab === 'offers' ||
      s.tab === 'ordering'
        ? s.tab
        : 'details',
    q: typeof s.q === 'string' && s.q !== '' ? s.q : undefined,
  }),
  component: VendorDetailScreen,
});

function VendorDetailScreen() {
  const { id } = Route.useParams();
  const vendorId = Number(id);
  const search = Route.useSearch();
  const tab: Tab = search.tab ?? 'details';
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();

  const [deactivateOpen, setDeactivateOpen] = useState(false);

  useShortcutHint([{ keys: 'Esc', label: 'back to vendors' }]);

  const vendor = useQuery({
    queryKey: vendorKeys.detail(vendorId),
    queryFn: () => api.get<VendorDetail>(`/vendors/${vendorId}`),
  });

  const setActive = useMutation({
    mutationFn: (active: boolean) =>
      active
        ? api.put<VendorDetail>(`/vendors/${vendorId}`, { active: true })
        : api.del<VendorDetail & { warnings?: Array<{ message: string }> }>(
            `/vendors/${vendorId}`
          ),
    onSuccess: (data, active) => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all });
      setDeactivateOpen(false);
      toast.success(active ? 'Vendor reactivated.' : 'Vendor deactivated.');
      // TASK-0249: deactivating a vendor with open orders says so (they stay open).
      const warnings = (data as { warnings?: Array<{ message: string }> }).warnings ?? [];
      for (const warning of warnings) toast.warning(warning.message);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (vendor.isPending) {
    return (
      <Page>
        <PageHeader eyebrow="Vendors" title="Loading…" />
        <PageBody>
          <Skeleton className="h-64 w-full" />
        </PageBody>
      </Page>
    );
  }

  if (vendor.isError || !vendor.data) {
    return (
      <Page>
        <PageHeader
          eyebrow="Vendors"
          title={`Vendor #${vendorId}`}
          actions={
            <Button size="sm" asChild icon={<ArrowLeft className="size-3.5" />}>
              <Link to="/vendors" search={{}}>
                Back
              </Link>
            </Button>
          }
        />
        <PageBody>
          <ErrorState
            title="Could not load this vendor"
            message={errorMessage(vendor.error)}
            onRetry={() => void vendor.refetch()}
          />
        </PageBody>
      </Page>
    );
  }

  const v = vendor.data;

  return (
    <Page>
      <PageHeader
        eyebrow={
          <Link to="/vendors" search={{}} className="hover:text-ink">
            Vendors
          </Link>
        }
        title={
          <span className="flex items-center gap-3">
            {v.name}
            {v.active === false ? <Badge tone="negative">Inactive</Badge> : null}
            {v.invoiceParsingNotesAuto ? (
              <Badge tone="warn" dot>
                Parser note to review
              </Badge>
            ) : null}
          </span>
        }
        subtitle={
          [profileSummary(v), v.terms ? `Terms ${v.terms}` : null]
            .filter(Boolean)
            .join(' · ') || undefined
        }
        tabs={
          // UX-1 (TASK-0240): new names, unchanged `?tab=` values, so old links still
          // open the same tab (`catalog` is Products we buy; `offers` is Their catalog).
          <SubNav>
            <SubNavItem
              active={tab === 'details'}
              onClick={() => void navigate({ search: { tab: 'details' } })}
            >
              Overview
            </SubNavItem>
            {/* TASK-0249 (INC-1c): How we order, between Overview and Products we buy. */}
            <SubNavItem
              active={tab === 'ordering'}
              onClick={() => void navigate({ search: { tab: 'ordering' } })}
            >
              How we order
            </SubNavItem>
            <SubNavItem
              active={tab === 'catalog'}
              count={v.items?.length ?? 0}
              onClick={() => void navigate({ search: { tab: 'catalog' } })}
            >
              Products we buy
            </SubNavItem>
            <SubNavItem
              active={tab === 'offers'}
              onClick={() => void navigate({ search: { tab: 'offers' } })}
            >
              Their catalog
            </SubNavItem>
            <SubNavItem
              active={tab === 'parsing'}
              onClick={() => void navigate({ search: { tab: 'parsing' } })}
            >
              Invoice reading
            </SubNavItem>
          </SubNav>
        }
        actions={
          <>
            <Button size="sm" variant="primary" asChild>
              <Link
                to="/purchasing/guide/$vendorId"
                params={{ vendorId: String(v.id) }}
                search={{}}
              >
                Order guide
              </Link>
            </Button>
            <Button size="sm" variant="default" asChild>
              <Link to="/purchasing/new" search={{ vendorId: v.id }}>
                New PO
              </Link>
            </Button>
            <Menu
              trigger={
                <Button size="sm" variant="default">
                  Import
                  <ChevronDown className="size-3.5 opacity-70" />
                </Button>
              }
            >
              <MenuItem asChild>
                <Link to="/purchasing/import" search={{ vendorId: v.id }}>
                  Invoice
                </Link>
              </MenuItem>
              <MenuItem asChild>
                <Link to="/vendors/catalogs" search={{ vendorId: v.id }}>
                  Catalog
                </Link>
              </MenuItem>
            </Menu>
            <Menu
              trigger={
                <Button
                  size="icon-sm"
                  variant="ghost"
                  aria-label="More vendor actions"
                  title="More"
                >
                  <MoreHorizontal className="size-3.5" />
                </Button>
              }
            >
              {v.active === false ? (
                <MenuItem
                  icon={<RotateCcw className="size-3.5" />}
                  onSelect={() => setActive.mutate(true)}
                >
                  Reactivate
                </MenuItem>
              ) : (
                <MenuItem tone="negative" onSelect={() => setDeactivateOpen(true)}>
                  Deactivate…
                </MenuItem>
              )}
            </Menu>
          </>
        }
      />

      <PageBody>
        {tab === 'details' ? <VendorForm vendor={v} /> : null}
        {tab === 'ordering' ? <HowWeOrderTab vendor={v} /> : null}
        {tab === 'catalog' ? <ProductsWeBuyTab vendor={v} /> : null}
        {tab === 'offers' ? <OffersTab vendor={v} initialQ={search.q ?? ''} /> : null}
        {tab === 'parsing' ? <ParsingTab vendor={v} /> : null}
      </PageBody>

      <ConfirmDialog
        open={deactivateOpen}
        onOpenChange={setDeactivateOpen}
        title={`Deactivate ${v.name}?`}
        confirmLabel="Deactivate"
        loading={setActive.isPending}
        consequences={[
          'The vendor disappears from vendor pickers on new POs and invoice imports.',
          'Existing purchase orders and product links are untouched.',
          'Reversible — reactivate from this page at any time.',
        ]}
        onConfirm={() => setActive.mutate(false)}
      />
    </Page>
  );
}

/* ── Details ───────────────────────────────────────────────────────────────── */

const TEXT_FIELDS = [
  ['name', 'Name'],
  ['contactName', 'Contact'],
  ['phone', 'Phone'],
  ['phoneExt', 'Extension'],
  ['fax', 'Fax'],
  ['email', 'Email'],
  ['terms', 'Terms'],
  ['addressLine1', 'Address line 1'],
  ['addressLine2', 'Address line 2'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip', 'ZIP'],
] as const;

type TextKey = (typeof TEXT_FIELDS)[number][0];

export function VendorForm({ vendor }: { vendor: VendorDetail }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Record<TextKey, string>>(() => readForm(vendor));
  const [notes, setNotes] = useState(vendor.notes ?? '');
  // TASK-0249: how deliveries are checked in and our account number moved to How we order.

  useEffect(() => {
    setForm(readForm(vendor));
    setNotes(vendor.notes ?? '');
  }, [vendor]);

  const dirty =
    TEXT_FIELDS.some(([key]) => form[key] !== (vendor[key] ?? '')) ||
    notes !== (vendor.notes ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.put<VendorDetail>(`/vendors/${vendor.id}`, {
        ...Object.fromEntries(TEXT_FIELDS.map(([key]) => [key, form[key]])),
        notes,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all });
      toast.success('Vendor saved.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <Panel
        title="Vendor details"
        footer={
          <div className="flex items-center justify-between">
            <span>{dirty ? 'Unsaved changes' : 'Saved'}</span>
            <Button
              size="xs"
              variant="primary"
              disabled={!dirty || form.name.trim() === ''}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="grid gap-3 sm:grid-cols-2">
          {TEXT_FIELDS.map(([key, label]) => (
            <Field
              key={key}
              label={label}
              required={key === 'name'}
              className={
                key === 'addressLine1' || key === 'addressLine2' ? 'sm:col-span-2' : ''
              }
              hint={
                key === 'email' || key === 'city' || key === 'state' || key === 'zip'
                  ? 'Blank clears the field.'
                  : undefined
              }
            >
              <Input
                size="sm"
                value={form[key]}
                invalid={key === 'name' && form.name.trim() === ''}
                onChange={(e) => {
                  const value = e.currentTarget.value;
                  setForm((prev) => ({ ...prev, [key]: value }));
                }}
              />
            </Field>
          ))}
        </div>
      </Panel>

      <Panel
        title="Internal notes"
        description="Not shown to the vendor, not fed to the parser."
      >
        <Textarea
          rows={10}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </Panel>
    </div>
  );
}

function readForm(vendor: VendorDetail): Record<TextKey, string> {
  const out = {} as Record<TextKey, string>;
  for (const [key] of TEXT_FIELDS) out[key] = vendor[key] ?? '';
  return out;
}

/* ── Invoice reading notes ─────────────────────────────────────────────────── */

function ParsingTab({ vendor }: { vendor: VendorDetail }) {
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState(vendor.invoiceParsingNotes ?? '');
  const [candidate, setCandidate] = useState(vendor.invoiceParsingNotesAuto ?? '');
  const [forcePdf, setForcePdf] = useState(vendor.forcePdfFileMode ?? false);
  const [discardOpen, setDiscardOpen] = useState(false);

  useEffect(() => {
    setNotes(vendor.invoiceParsingNotes ?? '');
    setCandidate(vendor.invoiceParsingNotesAuto ?? '');
    setForcePdf(vendor.forcePdfFileMode ?? false);
  }, [vendor]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: vendorKeys.all });

  const save = useMutation({
    mutationFn: () =>
      api.put<VendorDetail>(`/vendors/${vendor.id}`, {
        invoiceParsingNotes: notes.trim() === '' ? null : notes,
        forcePdfFileMode: forcePdf,
      }),
    onSuccess: () => {
      void invalidate();
      toast.success('Parsing instructions saved. They apply to the next parse.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const promote = useMutation({
    mutationFn: () =>
      api.post<VendorDetail>(`/vendors/${vendor.id}/invoice-parsing-notes/promote`, {
        text: candidate.trim(),
      }),
    onSuccess: () => {
      void invalidate();
      toast.success('Promoted — the parser will use this note from the next parse on.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const discard = useMutation({
    mutationFn: () =>
      api.post<VendorDetail>(`/vendors/${vendor.id}/invoice-parsing-notes/discard`),
    onSuccess: () => {
      void invalidate();
      setDiscardOpen(false);
      toast.success('Candidate note discarded.');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const dirty =
    notes !== (vendor.invoiceParsingNotes ?? '') ||
    forcePdf !== (vendor.forcePdfFileMode ?? false);

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel
        title="Parsing instructions — in use"
        description="Injected into every parse of this vendor's invoices."
        footer={
          <div className="flex items-center justify-between">
            <span>{dirty ? 'Unsaved changes' : 'Saved'}</span>
            <Button
              size="xs"
              variant="primary"
              disabled={!dirty}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              Save
            </Button>
          </div>
        }
      >
        <div className="space-y-3">
          <Textarea
            rows={10}
            value={notes}
            maxLength={4096}
            placeholder='e.g. The rightmost price column is the net case price, not per unit. Ignore the "list" column.'
            onChange={(e) => setNotes(e.currentTarget.value)}
          />
          <Field
            label="PDF handling"
            hint="Skips the cheap text pass and sends the PDF as an image. Use it when the extracted text comes back scrambled."
          >
            <Switch
              checked={forcePdf}
              onCheckedChange={setForcePdf}
              label="Always parse PDFs as images"
            />
          </Field>
        </div>
      </Panel>

      <Panel
        title="Parser observation — pending review"
        description="Written by the parser. It is NOT used until you promote it."
        actions={
          vendor.invoiceParsingNotesAuto ? (
            <Badge tone="warn" dot>
              Awaiting review
            </Badge>
          ) : null
        }
      >
        {vendor.invoiceParsingNotesAuto ? (
          <div className="space-y-3">
            <p className="text-2xs text-ink-faint">
              Observed {relativeTime(vendor.invoiceParsingNotesAutoAt)}
              {vendor.invoiceParsingNotesAutoSourcePoId ? (
                <>
                  {' '}
                  on{' '}
                  <Link
                    to="/purchasing/$id"
                    params={{ id: String(vendor.invoiceParsingNotesAutoSourcePoId) }}
                    search={{ mode: 'lines' as const }}
                    className="text-signal-ink hover:underline"
                  >
                    PO #{vendor.invoiceParsingNotesAutoSourcePoId}
                  </Link>
                </>
              ) : null}
              . Edit it before promoting if it needs sharpening.
            </p>
            <Textarea
              rows={8}
              value={candidate}
              maxLength={4096}
              onChange={(e) => setCandidate(e.currentTarget.value)}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="primary"
                icon={<Sparkles className="size-3.5" />}
                disabled={candidate.trim() === ''}
                loading={promote.isPending}
                onClick={() => promote.mutate()}
              >
                Promote to instructions
              </Button>
              <Button size="sm" variant="danger" onClick={() => setDiscardOpen(true)}>
                Discard
              </Button>
            </div>
            <p className="border-line bg-surface-sunken text-2xs text-ink-muted rounded border p-2 leading-relaxed">
              Promoting <em>replaces</em> the instructions on the left with this text — it
              does not append. Copy anything you want to keep across first.
            </p>
          </div>
        ) : (
          <p className="text-ink-faint text-xs">
            Nothing pending. When the parser meets an unfamiliar layout on this vendor's
            paperwork it leaves a note here for you to review.
          </p>
        )}
      </Panel>

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title="Discard the parser's note?"
        confirmLabel="Discard"
        loading={discard.isPending}
        consequences={[
          'Clears the pending observation.',
          'Your saved parsing instructions are untouched.',
          'The parser may raise the same observation again on the next invoice.',
        ]}
        onConfirm={() => discard.mutate()}
      />
    </div>
  );
}
